import React from "react";
import WeatherCommandDashboard from "@/components/weather/WeatherCommandDashboard";
import SailingIntelligencePanel from "@/components/weather/SailingIntelligencePanel";
import MarineCharts from "@/components/weather/MarineCharts";
import WeatherIntelligenceMap from "@/components/weather/WeatherIntelligenceMap";
import { useMarineWeather, beaufortFromKnots, sailingSafety } from "@/components/weather/useMarineWeather";
import { useUserLocation } from "@/hooks/useUserLocation";
import { getPublicWeatherTranslations } from "@/i18n";
import ContextualUpgradePrompt from "@/components/subscription/ContextualUpgradePrompt";

const DEFAULT_LAT = 32.0853;
const DEFAULT_LNG = 34.7818;

const ForecastToolsPanel = React.lazy(() => import("@/components/weather/forecast-tools/ForecastToolsPanel"));
const PublicWeatherOverviewExtras = React.lazy(() => import("@/components/public/PublicWeatherOverviewExtras"));
const CommunityWeatherHub = React.lazy(() => import("@/components/weather/CommunityWeatherHub"));

const localizedWeatherPrompt = {
  en: { title: "Now turn today’s weather into a safer departure decision.", text: "You checked the free live conditions. Premium helps connect wind, waves and timing into fewer bad sailing windows.", bullets: ["Avoid bad sailing windows", "Save time before departure", "Plan safer trips"], sourceNote: (city, source) => `Showing atmospheric weather for ${city} with marine model guidance from ${source}. Verify conditions with official local marine forecasts before departure.` },
  he: { title: "עכשיו הפכו את מזג האוויר של היום להחלטת יציאה בטוחה יותר.", text: "בדקתם את התנאים החיים בחינם. פרימיום מחבר רוח, גלים ותזמון כדי לצמצם חלונות שיט גרועים.", bullets: ["הימנעו מחלונות שיט גרועים", "חסכו זמן לפני יציאה", "תכננו הפלגות בטוחות יותר"], sourceNote: (city, source) => `מוצג מזג אוויר אטמוספרי עבור ${city} עם הנחיית מודל ימי מ-${source}. ודאו תנאים מול תחזיות ימיות רשמיות מקומיות לפני יציאה.` },
  es: { title: "Convierte el tiempo de hoy en una decisión de salida más segura.", text: "Ya viste las condiciones en vivo gratis. Premium conecta viento, olas y timing para evitar peores ventanas de navegación.", bullets: ["Evita malas ventanas", "Ahorra tiempo antes de salir", "Planifica travesías más seguras"], sourceNote: (city, source) => `Mostrando tiempo atmosférico para ${city} con guía de modelo marino desde ${source}. Verifica condiciones con pronósticos marinos oficiales locales antes de salir.` },
  it: { title: "Trasforma il meteo di oggi in una decisione di partenza più sicura.", text: "Hai controllato le condizioni live gratuite. Premium collega vento, onde e tempi per ridurre le finestre di navigazione sfavorevoli.", bullets: ["Evita finestre sfavorevoli", "Risparmia tempo prima della partenza", "Pianifica viaggi più sicuri"], sourceNote: (city, source) => `Mostriamo meteo atmosferico per ${city} con guida del modello marino da ${source}. Verifica le condizioni con previsioni marine ufficiali locali prima della partenza.` },
  fr: { title: "Transformez la météo du jour en décision de départ plus sûre.", text: "Vous avez vérifié les conditions live gratuites. Premium relie vent, vagues et timing pour réduire les mauvaises fenêtres de navigation.", bullets: ["Éviter les mauvaises fenêtres", "Gagner du temps avant le départ", "Planifier plus sûrement"], sourceNote: (city, source) => `Affichage de la météo atmosphérique pour ${city} avec guidance du modèle marin depuis ${source}. Vérifiez les conditions auprès des prévisions marines officielles locales avant le départ.` },
  de: { title: "Mach aus dem heutigen Wetter eine sicherere Ablegeentscheidung.", text: "Du hast die kostenlosen Live-Bedingungen geprüft. Premium verbindet Wind, Wellen und Timing, damit weniger schlechte Segelfenster bleiben.", bullets: ["Schlechte Fenster vermeiden", "Zeit vor dem Ablegen sparen", "Sicherer planen"], sourceNote: (city, source) => `Angezeigt wird atmosphärisches Wetter für ${city} mit maritimer Modellführung von ${source}. Prüfe vor dem Ablegen offizielle lokale Seewettervorhersagen.` },
  pt: { title: "Transforme o tempo de hoje numa decisão de partida mais segura.", text: "Você verificou as condições ao vivo grátis. O Premium conecta vento, ondas e timing para reduzir janelas ruins de navegação.", bullets: ["Evite janelas ruins", "Poupe tempo antes da partida", "Planeje viagens mais seguras"], sourceNote: (city, source) => `Mostrando meteorologia atmosférica para ${city} com orientação do modelo marinho de ${source}. Verifique previsões marítimas oficiais locais antes da partida.` },
  ar: { title: "حوّل طقس اليوم إلى قرار مغادرة أكثر أمانًا.", text: "لقد تحققت من الظروف الحية المجانية. يربط بريميوم الرياح والأمواج والتوقيت لتقليل نوافذ الإبحار السيئة.", bullets: ["تجنب نوافذ الإبحار السيئة", "وفّر الوقت قبل المغادرة", "خطط لرحلات أكثر أمانًا"], sourceNote: (city, source) => `يتم عرض الطقس الجوي لـ ${city} مع إرشاد النموذج البحري من ${source}. تحقق من الظروف عبر التوقعات البحرية الرسمية المحلية قبل المغادرة.` },
  el: { title: "Μετατρέψτε τον σημερινό καιρό σε ασφαλέστερη απόφαση αναχώρησης.", text: "Ελέγξατε τις δωρεάν ζωντανές συνθήκες. Το Premium συνδέει άνεμο, κύματα και χρόνο για λιγότερα κακά παράθυρα πλεύσης.", bullets: ["Αποφύγετε κακά παράθυρα", "Κερδίστε χρόνο πριν την αναχώρηση", "Σχεδιάστε ασφαλέστερα ταξίδια"], sourceNote: (city, source) => `Εμφανίζεται ατμοσφαιρικός καιρός για ${city} με θαλάσσια μοντελοποίηση από ${source}. Επιβεβαιώστε με επίσημες τοπικές θαλάσσιες προγνώσεις πριν την αναχώρηση.` },
  ru: { title: "Превратите сегодняшнюю погоду в более безопасное решение о выходе.", text: "Вы проверили бесплатные live-условия. Premium связывает ветер, волны и время, чтобы уменьшить риск плохих погодных окон.", bullets: ["Избегайте плохих окон", "Экономьте время перед выходом", "Планируйте безопаснее"], sourceNote: (city, source) => `Показана атмосферная погода для ${city} с морской модельной подсказкой от ${source}. Перед выходом сверяйтесь с официальными местными морскими прогнозами.` },
  tr: { title: "Bugünün havasını daha güvenli bir çıkış kararına dönüştürün.", text: "Ücretsiz canlı koşulları kontrol ettiniz. Premium rüzgarı, dalgayı ve zamanı birleştirerek kötü seyir pencerelerini azaltır.", bullets: ["Kötü seyir pencerelerinden kaçın", "Çıkış öncesi zaman kazan", "Daha güvenli seyir planla"], sourceNote: (city, source) => `${city} için atmosferik hava, ${source} kaynaklı deniz modeli rehberliğiyle gösteriliyor. Çıkıştan önce resmi yerel deniz tahminlerini doğrulayın.` },
  zh: { title: "把今天的天气转化为更安全的出航决策。", text: "你已查看免费的实时条件。高级版会把风、浪和时间连接起来，帮助避开更差的航行窗口。", bullets: ["避开糟糕航行窗口", "出发前节省时间", "规划更安全航程"], sourceNote: (city, source) => `正在显示 ${city} 的大气天气，并结合来自 ${source} 的海洋模型指导。出发前请核对当地官方海洋预报。` },
};

export default function PublicWeatherDashboard({ lang = "en" }) {
  const copy = getPublicWeatherTranslations(lang).shell;
  const upgradeCopy = localizedWeatherPrompt[lang];
  const { location: userLocation } = useUserLocation();
  const location = userLocation || { lat: DEFAULT_LAT, lng: DEFAULT_LNG };
  const { data, isLoading, isError } = useMarineWeather(location.lat, location.lng);
  const [locationName, setLocationName] = React.useState({ city: copy.currentLocation, country: "" });
  const [showEnhancements, setShowEnhancements] = React.useState(false);

  React.useEffect(() => {
    const loadEnhancements = () => setShowEnhancements(true);
    const idleId = window.requestIdleCallback
      ? window.requestIdleCallback(loadEnhancements, { timeout: 1800 })
      : window.setTimeout(loadEnhancements, 1200);

    return () => {
      if (window.cancelIdleCallback && typeof idleId === "number") window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, []);

  React.useEffect(() => {
    if (!location?.lat || !location?.lng) return;
    if (location.city) {
      setLocationName({ city: location.city, country: location.country || "" });
      return;
    }
    setLocationName({ city: copy.currentLocation, country: "" });

    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.lat}&lon=${location.lng}`)
      .then((res) => res.json())
      .then((json) => {
        setLocationName({
          city: json.address?.city || json.address?.town || json.address?.village || json.address?.municipality || copy.openSea,
          country: json.address?.country || "",
        });
      });
  }, [location?.lat, location?.lng, location?.city, location?.country, copy.currentLocation]);

  if (!upgradeCopy) return null;

  if (isLoading) {
    return (
      <section className="mt-10 rounded-3xl border border-border bg-card p-6 shadow-xl">
        <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{copy.liveDashboard}</p>
        <p className="mt-2 text-muted-foreground">{copy.loading}</p>
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section className="mt-10 rounded-3xl border border-border bg-card p-6 shadow-xl">
        <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{copy.liveDashboard}</p>
        <p className="mt-2 text-muted-foreground">{copy.unavailable}</p>
      </section>
    );
  }

  const cur = data?.forecast?.current ?? {};
  const marCur = data?.marine?.current ?? {};
  const windKnots = Math.round(cur.wind_speed_10m ?? 0);
  const gustKnots = Math.round(cur.wind_gusts_10m ?? 0);
  const waveM = +(marCur.wave_height ?? 0).toFixed(2);
  const beaufort = beaufortFromKnots(windKnots);
  const safety = sailingSafety(windKnots, waveM);

  return (
    <section className="mt-10">
      <div className="mb-5">
        <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{copy.liveDashboard}</p>
        <h2 className="mt-2 font-heading text-3xl font-black text-foreground">{copy.commandCenter}</h2>
        {data.marine_location?.name && (
          <p className="mt-2 text-sm font-semibold text-muted-foreground">
            {upgradeCopy.sourceNote(locationName.city, data.marine_location.name)}
          </p>
        )}
      </div>
      <WeatherCommandDashboard
        windKnots={windKnots}
        gustKnots={gustKnots}
        waveM={waveM}
        safety={safety}
        beaufort={beaufort}
        forecast={data.forecast}
        marine={data.marine}
        locationName={locationName}
        lang={lang}
      />
      <div className="mt-6">
        <ContextualUpgradePrompt
          title={upgradeCopy.title}
          text={upgradeCopy.text}
          bullets={upgradeCopy.bullets}
        />
      </div>
      <div className="mt-6">
        <SailingIntelligencePanel
          forecast={data.forecast}
          marine={data.marine}
          windKnots={windKnots}
          gustKnots={gustKnots}
          waveM={waveM}
          lang={lang}
        />
      </div>
      <div className="mt-8">
        <MarineCharts marine={data.marine} forecast={data.forecast} lang={lang} />
      </div>
      <React.Suspense fallback={<div className="mt-8 rounded-3xl border border-border bg-card/80 p-5 text-sm font-semibold text-muted-foreground">{getPublicWeatherTranslations(lang).radar.loading}</div>}>
        <div className="mt-8">
          <WeatherIntelligenceMap latitude={location.lat} longitude={location.lng} forecast={data.forecast} marine={data.marine} lang={lang} />
        </div>
      </React.Suspense>
      {showEnhancements ? (
        <React.Suspense fallback={<div className="mt-8 rounded-3xl border border-border bg-card/80 p-5 text-sm font-semibold text-muted-foreground">{getPublicWeatherTranslations(lang).forecastTools?.loading || ""}</div>}>
          <ForecastToolsPanel
            forecast={data.forecast}
            marine={data.marine}
            locationName={locationName}
            lang={lang}
          />
          <PublicWeatherOverviewExtras
            forecast={data.forecast}
            marine={data.marine}
            windKnots={windKnots}
            gustKnots={gustKnots}
            waveM={waveM}
            location={location}
            lang={lang}
          />
          <div className="mt-10">
            <CommunityWeatherHub
              latitude={location.lat}
              longitude={location.lng}
              locationName={locationName}
              lang={lang}
            />
          </div>
        </React.Suspense>
      ) : (
        <div className="mt-8 rounded-3xl border border-border bg-card/70 p-5 text-sm font-semibold text-muted-foreground">{getPublicWeatherTranslations(lang).forecastTools?.deferred || ""}</div>
      )}
    </section>
  );
}